/**
 * For single user selection use ControlledField - Combobox variant (see AddUserForm.tsx)
 */

import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  useTagPickerFilter,
} from '@fluentui/react-components';
import { Tag, Field, Tooltip } from '@fluentui/react-components';
import { LockClosed16Regular } from '@fluentui/react-icons';
import { forwardRef, LegacyRef, useState } from 'react';
import { useUsers } from '../api/users.api';
import Avatar from './Avatar';
import { TMultiUserSelectProps } from '../types/users.types';
import { withLockedUserIds } from '../helpers/withLockedUserIds.helper';

const MultiUserSelect = forwardRef(
  (
    {
      label,
      selectedUserIds,
      setSelectedUserIds,
      setSelectedUserId,
      group,
      customGroupId,
      lockedUserIds = [],
      lockedUserMessage = 'This user cannot be removed.',
      excludedUserIds = [],
      fieldProps = {},
      disabled = false,
      className,
      size = 'large',
    }: TMultiUserSelectProps,
    ref
  ) => {
    const selectedUserIdsValue = selectedUserIds
      ? typeof selectedUserIds === 'string'
        ? [selectedUserIds]
        : selectedUserIds
      : [];
    const selectedUserIdsArr = withLockedUserIds(selectedUserIdsValue, lockedUserIds);
    const excludedUserIdsSet = new Set(excludedUserIds);

    const { users, userMap } = useUsers();
    const filteredUsers = (users || [])
      .filter((user) => user.inviteAccepted)
      .filter((user) => !group || user.groups.includes(group))
      .filter((user) => !customGroupId || user.customGroups.includes(customGroupId))
      .filter((user) => !excludedUserIdsSet.has(user._id));

    const [query, setQuery] = useState<string>('');
    const onOptionSelect: TagPickerProps['onOptionSelect'] = (_, data) => {
      if (data.value === 'no-matches') return;
      const nextSelectedOptions = withLockedUserIds(data.selectedOptions, lockedUserIds);
      setSelectedUserIds?.(nextSelectedOptions);
      setSelectedUserId?.(selectedUserIdsArr.includes(data.value) ? null : data.value);
      setQuery('');
    };

    const children = useTagPickerFilter({
      query,
      options: filteredUsers.map((user) => user._id),
      noOptionsElement: <TagPickerOption value="no-matches">No matching user.</TagPickerOption>,
      renderOption: (userId) => {
        const user = userMap && userMap[userId];
        return (
          <TagPickerOption
            media={<Avatar user={user} className="mr-2" />}
            value={userId}
            text={user?.name}
            key={userId}
          >
            {user?.name || 'Unknown'}
          </TagPickerOption>
        );
      },
      filter: (userId) =>
        Boolean(
          !selectedUserIdsArr.includes(userId) &&
            userMap &&
            userMap[userId]?.name?.toLowerCase()?.includes(query.toLowerCase())
        ),
    });

    const tagPicker = (
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedUserIdsArr}
        size={size}
        disabled={disabled}
        positioning={{ position: 'below', autoSize: 'height', pinned: true }}
      >
        <TagPickerControl>
          <TagPickerGroup aria-label="Selected users">
            {selectedUserIdsArr.map((userId) => {
              const locked = lockedUserIds.includes(userId);
              const tag = (
                <Tag
                  key={userId}
                  shape="rounded"
                  appearance={locked ? 'outline' : undefined}
                  disabled={locked}
                  icon={locked ? <LockClosed16Regular /> : undefined}
                  media={<Avatar user={userMap && userMap[userId]} />}
                  value={userId}
                >
                  {(userMap && userMap[userId]?.name) || 'Unknown'}
                </Tag>
              );
              return locked ? (
                <Tooltip key={userId} content={lockedUserMessage} relationship="description" withArrow>
                  {tag}
                </Tooltip>
              ) : (
                tag
              );
            })}
          </TagPickerGroup>
          <TagPickerInput
            aria-label="Select users"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required={false}
          />
        </TagPickerControl>
        <TagPickerList>{children}</TagPickerList>
      </TagPicker>
    );
    if (!label)
      return (
        // tag-picker has no className
        <div {...fieldProps} ref={ref as LegacyRef<HTMLDivElement> | undefined} className={className}>
          {tagPicker}
        </div>
      );

    return (
      <Field label={label} {...fieldProps} ref={ref as LegacyRef<HTMLDivElement> | undefined} className={className}>
        {tagPicker}
      </Field>
    );
  }
);

export default MultiUserSelect;
